// src/services/Integrations/OzkIntegrationService.ts

import { CALCULATOR_BASE_URL } from "../../constants/apiBases.ts";

export interface OzkPadej {
  padejDate: string;
  padejPremiumVal: string;
  padejPremium: number;
}

export interface OzkTariffResponse {
  reqID: string;
  writtenPremium: number;
  basicPremium: number;
  calculatedPremium: number;
  gfPremium: number;
  taxAmount: number;
  padeji: { padej: OzkPadej[] };
  status?: { status: number; info?: any[] };
}

export interface OzkIssueResponse {
  reqID: string;
  upn: string;
  upnGreenCard: string;
  status: {
    status: number;
    info: { type: string; code: string; description: string }[];
  };
}

export interface OzkTariffRequest {
  contractDate: string;
  comencingDate: string;
  expiringDate: string;
  periodId: string;
  vhTypeIntId: string;
  custFtypeId: string;
  custLocationId: string;
  custPin: string;
  custTrafficaccidents: string;
  vhSeatsCount: number;
  vhEngineCapacity: number;
  vhManifactureYear: number;
  vhMaxWeightF1: number;
  vhRegistrationTypeId: string;
  driverFtypeId: string;
  driverPin: string;
  numberOfInstallments: number;
  vhPurpose: string;
  vhRegNo: string;
  vhRegCertNo: string;
  vhInsObjTypeId: string;
  acquirerFtypeId: string;
  acquirerPin: string;
  vehicleUserFtypeId: string;
  vehicleUserPin: string;
  newOwnerPin: string;
}

export interface OzkIssueRequest {
  mtplPolicy: {
    contractDate: string;
    comencingDate: string;
    expiringDate: string;
    numberOfInstallments: number;
    vhHostCountry: string;
    vhDriverType: string;
    periodId: string;
    vhInsObjTypeId: string;
    payType: string;
    vehicle: {
      vhVin: string;
      vhRegNo: string;
      vhFirstRegDate: string;
      vhManifactureYear: number;
      vhBrandDescription: string;
      vhModelDescription: string;
      vhSeatsCount: number;
      vhMaxWeight: number;
      vhEngineCapacity: number;
      vhMaxPower: number;
      vhRegistrationTypeId: string;
      vhPurposeId: string;
      vhRegCertNumber: string;
      vhMaxWeightF1: number;
    };
    holder: OzkPerson;
    owner: OzkPerson;
    driver: OzkPerson | Record<string, never>;
    vehicleUser: OzkPerson | Record<string, never>;
    flRenew: boolean;
    specialClauses: string;
    acquirer: string;
    newOwner: string;
    stikeri?: { stiker: OzkStiker[] };
    sertifikati?: { sertifikat: OzkSertifikat[] };
  };
}

export interface OzkPerson {
  custName: string;
  custSurname: string;
  custFamily: string;
  custPin: string;
  custCountryId: string;
  custLocationId: string;
  custPostcode: string;
  custStreet: string;
  custFtypeId: string;
  custTypeId?: string;
  custTztypeId?: string;
}

export interface OzkStiker {
  sdocTypeId: string;
  serialNum: string;
  comencingDate: string;
  expiringDate: string;
}

export interface OzkSertifikat {
  serialNum: string;
  comencingDate: string;
  expiringDate: string;
  gcsTervalidityId: string;
  sdocTypeId: string;
}

type NomenclatureDictionary = Record<string, string>;

interface NomenclatureRow {
  id: string;
  name: string;
  active: boolean;
}
interface NomenclatureDetailsResponse {
  row: NomenclatureRow[];
}

const BASE = `${CALCULATOR_BASE_URL}/ozk`;

const post = async <T>(jwt: string, path: string, body: object): Promise<T> => {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`OZK ${path}: HTTP ${res.status}${text ? ` - ${text}` : ""}`);
  }
  return res.json() as Promise<T>;
};

const toDict = (rows: NomenclatureRow[]): NomenclatureDictionary =>
  rows
    .filter((r) => r.active)
    .reduce((acc, r) => ({ ...acc, [r.id]: r.name }), {});

const getNomenclature = async (
  jwt: string,
  nomName: string
): Promise<NomenclatureDictionary> => {
  const data = await post<NomenclatureDetailsResponse>(jwt, "/getNom", {
    nomName,
  });
  return toDict(data.row);
};

const getTariff = async (
  jwt: string,
  payload: OzkTariffRequest
): Promise<OzkTariffResponse> => {
  const res = await post<OzkTariffResponse>(jwt, "/MTPLPolicyGetTariff", payload);
  // OZK business errors can arrive on HTTP 200 as { status: { status: 1, info } }
  if (res.status && res.status.status !== 0) {
    const msg =
      res.status.info?.map((i: any) => i.description).join("; ") ??
      "Грешка при тарифиране";
    throw new Error(msg);
  }
  return res;
};

const issuePolicy = (
  jwt: string,
  payload: OzkIssueRequest
): Promise<OzkIssueResponse> => post(jwt, "/MTPLPolicyIssue", payload);

const printPolicy = async (jwt: string, reqID: string): Promise<Blob> => {
  const res = await fetch(`${BASE}/print/${reqID}`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!res.ok) throw new Error(`OZK print: HTTP ${res.status}`);
  return res.blob();
};

export const OzkIntegrationService = {
  getTariff,
  issuePolicy,
  printPolicy,
  getNomenclature,
  getAllCities: (jwt: string) => getNomenclature(jwt, "NOM_CITY"),
  getAllPostcodes: (jwt: string) => getNomenclature(jwt, "NOM_CITYPOSTCODE"),
  getAllVehiclePurposes: (jwt: string) =>
    getNomenclature(jwt, "NOM_VEHICLEPURPOSE"),
  getAllCustomerTypes: (jwt: string) => getNomenclature(jwt, "NOM_CUSTTYPE"),
  getAllVehicleRegistrationTypes: (jwt: string) =>
    getNomenclature(jwt, "NOM_VEHICLEREGISTRATIONTYPE"),
  getAllFtypes: (jwt: string) => getNomenclature(jwt, "NOM_FTYPE"),
};
